import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { getLocalSelectedDevice, setLocalSelectedDevice } from '../../../../utils/selectedDevice';
import {
  QUERY_ALL_DEVICES,
  QUERY_DEVICE_BY_SCODE,
  QUERY_FEATURES_AVAILABILITY,
  QUERY_MAIN_DEVICE,
} from '../../../../graphql/device';
import useIsMountedRef from '../../../../hooks/useIsMountedRef';
import { setDeviceDataInLocal } from '../services';

export const useAccountPopover = () => {
  const { t } = useTranslation();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');
  const [isFeatureAvail, setIsFeatureAvail] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const mainDevice = useRef({});

  // -------------------
  const [getDevices, { loading: devicesSkeleton }] = useLazyQuery(QUERY_ALL_DEVICES);
  const [getDeviceByScode, { loading: deviceLoading }] = useLazyQuery(QUERY_DEVICE_BY_SCODE);
  const [getMainDevice, { loading: mainDeviceLoading }] = useLazyQuery(QUERY_MAIN_DEVICE);
  const [getFeaturesAvailability] = useLazyQuery(QUERY_FEATURES_AVAILABILITY);
  // -------------------

  const fetchAvailability = async () => {
    const { data } = await getFeaturesAvailability({
      context: {
        uri: process.env.REACT_APP_GRAPHQL_URI,
      },
    });
    setIsFeatureAvail(data.devices.featureAvailable ?? false);
  };
  const fetchDevices = async () => {
    if (isFeatureAvail) {
      try {
        const { data: devicesData } = await getDevices({
          context: {
            uri: process.env.REACT_APP_GRAPHQL_URI,
          },
        });

        const allDevices = devicesData.devices.all || [];
        const sortedData = allDevices.sort((a, b) => b.IsActive - a.IsActive);
        // Reorder the devices
        setDevices(() => {
          const filteredDevices = sortedData.filter(
            (d) => d.Scode !== selectedDevice.Scode && d.Scode !== mainDevice.current.Scode && !d.isPrimary
          );
          if (selectedDevice.isPrimary) {
            return [selectedDevice, ...filteredDevices];
          }
          return [selectedDevice, mainDevice.current, ...filteredDevices];
        });
      } catch (err) {
        setError(err?.graphQLErrors?.[0]?.message || t('An unexpected error has occurred'));
      }
    } else {
      setDevices([mainDevice.current]);
    }
  };

  const fetchMainDevice = async () => {
    const { data } = await getMainDevice({
      context: {
        uri: process.env.REACT_APP_GRAPHQL_URI,
      },
    });
    mainDevice.current = { ...data.devices.me, isPrimary: true };
    const select = getLocalSelectedDevice();
    setSelectedDevice(select ?? { ...data.devices.me, isPrimary: true });
    localStorage.setItem('PSC' ,data.devices.me.Scode)
    if(!select){
      setLocalSelectedDevice({ ...data.devices.me, isPrimary: true });
    }
  };

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
    if (event.currentTarget) {
      fetchDevices();
    }
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      // navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const successSelectDeviceActions = (selectedDevice, error, data, deviceType) => {
    if (!error) {
      setLocalSelectedDevice(selectedDevice);
      handleClose();
      setDeviceDataInLocal(data, deviceType)
      window.location.reload();
    }
  };

  const handleSelectDevice = async (device) => {
    if (device.IsActive && device.Name !== selectedDevice.Name) {
      setSelectedDevice(device);
      if (device.isPrimary) {
        const { data, error } = await getMainDevice({
          context: {
            uri: process.env.REACT_APP_GRAPHQL_URI,
          },
        });

        successSelectDeviceActions(device, error, data, 'me');
      } else {
        const { data, error } = await getDeviceByScode({
          variables: { name: device.Name, scode: device.Scode },
          context: {
            uri: process.env.REACT_APP_GRAPHQL_URI,
          },
        });
        successSelectDeviceActions(device, error, data, 'get');
      }
    }
  };

  useEffect(() => {
    fetchMainDevice();
    fetchAvailability();
  }, []);

  return {
    handleOpen,
    handleLogout,
    handleSelectDevice,
    open,
    error,
    devices,
    selectedDevice,
    devicesSkeleton,
    deviceLoading,
    mainDeviceLoading,
    handleClose,
  };
};
